/**
 * Business Hours Service
 *
 * Manages business hours from the API and provides utility functions
 * to determine opening hours and current status.
 */

const API_HOURS_URL = 'https://api.lealchapaypintura.com/api/public/hours';

/**
 * Fetch business hours from API
 * @returns {Promise<Object>} Business hours data
 */
export const fetchBusinessHours = async () => {
  try {
    const response = await fetch(API_HOURS_URL);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const json = await response.json();
    if (!json || json.success !== true || !json.data) {
      throw new Error('Invalid business hours response');
    }
    return json.data;
  } catch (error) {
    console.error('Error fetching business hours:', error);
    // No fallback schedule: bubble up so UI can decide what to show.
    throw error;
  }
};

/**
 * Get day name in English from day index (0-6, where 0 is Sunday)
 * @param {number} dayIndex - Day index (0-6)
 * @returns {string} Day name in lowercase
 */
const getDayName = (dayIndex) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[dayIndex];
};

/**
 * Get day name in Spanish from day index (0-6, where 0 is Sunday)
 * @param {number} dayIndex - Day index (0-6)
 * @returns {string} Day name in Spanish
 */
const getDayNameSpanish = (dayIndex) => {
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[dayIndex];
};

/**
 * Parse time string to minutes since midnight
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {number} Minutes since midnight
 */
const parseTimeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Check if business is currently open
 * @param {Object} hoursData - Hours data from API
 * @returns {boolean} True if open, false if closed
 */
export const isOpenNow = (hoursData) => {
  if (!hoursData) return false;
  if (typeof hoursData?.status?.isOpen === 'boolean') return hoursData.status.isOpen;

  // Fallback computation if API doesn't provide status
  if (!Array.isArray(hoursData?.schedule)) return false;

  const now = new Date();
  const dayName = getDayName(now.getDay());
  const dayHours = hoursData.schedule.find((d) => d && d.day === dayName);
  if (!dayHours || dayHours.closed || !dayHours.open || !dayHours.close) return false;

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = parseTimeToMinutes(dayHours.open);
  const closeMinutes = parseTimeToMinutes(dayHours.close);
  return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
};

/**
 * Get today's hours
 * @param {Object} hoursData - Hours data from API
 * @returns {Object} Today's hours info
 */
export const getTodayHours = (hoursData) => {
  if (!hoursData || !Array.isArray(hoursData?.schedule)) return null;

  const now = new Date();
  const dayName = getDayName(now.getDay());
  const dayNameSpanish = getDayNameSpanish(now.getDay());
  const dayHours = hoursData.schedule.find((d) => d && d.day === dayName);
  if (!dayHours) return null;

  return {
    day: dayNameSpanish,
    open: dayHours.open,
    close: dayHours.close,
    closed: !!dayHours.closed
  };
};

/**
 * Format hours for display
 * Note: This assumes Monday-Friday have the same hours based on typical business schedules.
 * If weekdays have different hours in the future, this function should be updated.
 * @param {Object} hoursData - Hours data from API
 * @returns {string} Formatted hours string
 */
export const formatHoursDisplay = (hoursData) => {
  if (!hoursData) return '';
  return typeof hoursData.summary === 'string' ? hoursData.summary : '';
};

/**
 * Get status text (Abierto/Cerrado)
 * @param {Object} hoursData - Hours data from API
 * @returns {string} Status text in Spanish
 */
export const getStatusText = (hoursData) => {
  if (!hoursData) return '';
  return typeof hoursData?.status?.message === 'string' ? hoursData.status.message : '';
};

/**
 * Format today's hours for display
 * @param {Object} hoursData - Hours data from API
 * @returns {string} Formatted today's hours string
 */
export const formatTodayHours = (hoursData) => {
  if (!hoursData || !hoursData.hours) return '';

  const todayHours = getTodayHours(hoursData);
  if (!todayHours) return '';
  if (todayHours.closed) return `${todayHours.day}: Cerrado`;

  return `${todayHours.day}: ${todayHours.open} - ${todayHours.close}`;
};
